"use client";

import Translations from "@translate/components/OnboardingWizard/Paywall/useOnboardingPaywallPurchase";
import { useCallback, useMemo, useState } from "react";
import { OspToast } from "@ospweb/sdk-ui";
import {
  MemberProductLevelEnum,
  MemberStatusEnum,
} from "@/src/features/api/memberApi";
import {
  useAppPurchaseFlow,
  useMemberFeature,
  useMemberUser,
  useShellRouter,
} from "@/src/hooks";
import {
  ProductPayCycle,
  ProductStatus,
  ProductType,
} from "@/src/components/Member/CurrentPlan/constants";
import { I18nUtil } from "@utils/i18n";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/src/config/constant";
import { MemberKeys } from "@/src/screens/Member";
import log from "logbeacon";
import {
  trackObPaywallPurchaseSuccess,
  trackObPaywallTrialStart,
} from "@/src/hooks/onboarding/onboardingAnalytics";
import { trackEvent } from "@/src/utils/tracking";
import { navigateToMemberPricingPage } from "@/src/utils/member";
import { EdgenModalCreateAble } from "@/src/components/EdgenModal";
import { opportunitiesQueryKeys } from "@/src/hooks/useOpportunities";
import type {
  UseOnboardingPaywallPurchaseParams,
  UseOnboardingPaywallPurchaseResult,
} from "./types";

const I18n = I18nUtil.createScoped(Translations);

export const useOnboardingPaywallPurchase = ({
  onClose,
  trackingModuleName,
  analyticsSource,
  onWebCheckoutRedirect,
}: UseOnboardingPaywallPurchaseParams): UseOnboardingPaywallPurchaseResult => {
  const queryClient = useQueryClient();
  const { data: memberProductData, trialList } = useMemberFeature();
  const { refetch: refetchMemberUser } = useMemberUser();
  const { purchaseInApp, shouldTryNativePayment } = useAppPurchaseFlow();
  const router = useShellRouter();
  const [isLoading, setIsLoading] = useState(false);

  const completeYearDisplayProduct = useMemo(() => {
    const yearProducts = (memberProductData || []).filter(
      (item) =>
        item.pay_cycle === ProductPayCycle.YEAR &&
        item.status === ProductStatus.ACTIVE &&
        item.type === ProductType.NORMAL &&
        item.level === MemberProductLevelEnum.EXPERT,
    );
    return yearProducts.length > 0 ? yearProducts[0] : undefined;
  }, [memberProductData]);

  const completeYearProduct = useMemo(() => {
    const trialProduct = trialList?.find(
      (item) => item.renewal_id === completeYearDisplayProduct?.renewal_id,
    );
    if (trialProduct) {
      return trialProduct;
    }
    return completeYearDisplayProduct;
  }, [completeYearDisplayProduct, trialList]);

  const annualDisplayPrice = Number(
    completeYearDisplayProduct?.price ?? completeYearProduct?.price ?? 0,
  );

  const isBuyTrialNow = completeYearProduct?.type === ProductType.TRIAL;

  const buttonText = useMemo(() => {
    if (isBuyTrialNow) {
      if (completeYearProduct?.price && Number(completeYearProduct.price) > 0) {
        return I18n.t("Try 7 Days for Just $%{var0}", {
          var0: Number(completeYearProduct.price).toFixed(2),
        });
      }
      return I18n.t("Start Your 7-Day Free Trial");
    }
    return I18n.t("Subscribe now");
  }, [completeYearProduct?.price, isBuyTrialNow]);

  const onUnlockSuccess = useCallback(async () => {
    const source = analyticsSource ?? "onboarding";
    trackObPaywallPurchaseSuccess(source, "");
    log.info("[OnboardingPaywall] onUnlockSuccess start", {
      shouldTryNativePayment,
    });
    if (shouldTryNativePayment) {
      const maxAttempts = 5;
      const intervalMs = 1000;
      let isActive = false;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const result = await refetchMemberUser();
        const status = result?.data?.status;
        log.info("[OnboardingPaywall] member poll", {
          attempt: attempt + 1,
          maxAttempts,
          status,
        });
        if (status === MemberStatusEnum.ACTIVE) {
          isActive = true;
          break;
        }
        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, intervalMs));
        }
      }
      if (!isActive) {
        log.warn(
          "[OnboardingPaywall] member not ACTIVE after polls, fallback pricing",
          { maxAttempts, intervalMs },
        );
        onWebCheckoutRedirect?.();
        onClose?.();
        navigateToMemberPricingPage(router, { tab: MemberKeys.PRICING });
        return;
      }
    }
    log.info("[OnboardingPaywall] onUnlockSuccess complete");
    EdgenModalCreateAble.end(true);
    await queryClient.invalidateQueries({
      queryKey: [...opportunitiesQueryKeys.all, "list"],
    });
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.MEMBER_USE_PLAN],
    });
  }, [
    analyticsSource,
    onClose,
    queryClient,
    refetchMemberUser,
    router,
    shouldTryNativePayment,
  ]);

  const handlePurchase = useCallback(() => {
    trackObPaywallTrialStart("");
    trackEvent("ob_paywall_trial_click", { subscription_id: "" });
    log.trace("[GTM_CLICK][spend_membership]", {
      module_name: trackingModuleName ?? "paywall_onboarding",
      trigger_symbol: "",
      current_balance: undefined,
    });

    if (shouldTryNativePayment) {
      if (!completeYearProduct) {
        onWebCheckoutRedirect?.();
        onClose?.();
        navigateToMemberPricingPage(router, { tab: MemberKeys.PRICING });
        return;
      }
      setIsLoading(true);
      void purchaseInApp(completeYearProduct, {
        onSuccess: () => {
          void onUnlockSuccess().finally(() => {
            setIsLoading(false);
          });
        },
      }).then((result) => {
        if (!result.success) {
          setIsLoading(false);
          if (result.fallbackToStripe) {
            onWebCheckoutRedirect?.();
            onClose?.();
            navigateToMemberPricingPage(router, { tab: MemberKeys.PRICING });
          } else {
            OspToast.error({
              content: result.error ?? I18n.t("Purchase failed."),
            });
          }
        }
      });
      return;
    }

    onWebCheckoutRedirect?.();
    onClose?.();
    navigateToMemberPricingPage(router, { tab: MemberKeys.PRICING });
  }, [
    completeYearProduct,
    onWebCheckoutRedirect,
    onClose,
    onUnlockSuccess,
    purchaseInApp,
    router,
    shouldTryNativePayment,
    trackingModuleName,
  ]);

  return {
    annualDisplayPrice,
    buttonText,
    isBuyTrialNow,
    isLoading,
    handlePurchase,
  };
};
